// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file FieldStatus.tsx
 * @input Uses React, stylex, theme tokens
 * @output Exports FieldStatus component, FieldStatusProps
 * @position Core implementation; consumed by Field, Switch, CheckboxInput, and the FieldStatus entrypoint
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/FieldStatus/FieldStatus.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Field/Field.doc.mjs (compat docs when public API changes)
 * - /packages/core/src/FieldStatus/index.ts (exports if types change)
 * - /packages/core/src/Field/index.ts (compat re-export if public API changes)
 * - /packages/cli/templates/blocks/components/FieldStatus/ (showcase blocks)
 */

'use client';

import React from 'react';
import * as stylex from '@stylexjs/stylex';
import { mergeProps } from "../utils/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { useEntryAnimation } from "../hooks/useEntryAnimation.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  base: {
    kMv6JI: "x9ynric",
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  },
  attached: {
    keoZOQ: "x1c40v9y",
    kLKAdn: "x1ak3mig",
    kGO01o: "x1wesfrj",
    kg3NbH: "xf314gf",
    kqGeR4: "x14lggfu",
    kYm2EN: "xtufxuj",
    $$css: true
  },
  detached: {
    keoZOQ: "xcsaf9d",
    k8WAf4: "xce4md1",
    kg3NbH: "xf314gf",
    kaIpWk: "xh6dtrn",
    $$css: true
  }
};
const colorStyles = {
  warning: {
    kWkggS: "x24i8r5",
    kMwMTN: "xdhq94a",
    $$css: true
  },
  error: {
    kWkggS: "x1pritpl",
    kMwMTN: "x1joocv1",
    $$css: true
  },
  success: {
    kWkggS: "xu13z74",
    kMwMTN: "xltfdvo",
    $$css: true
  }
};

/**
 * Extensible variant map for FieldStatus.
 *
 * Theme packages can add custom variants via TypeScript module augmentation:
 * @example
 * ```
 * declare module '@astryxdesign/core/FieldStatus' {
 *   interface FieldStatusVariantMap {
 *     'inline': true;
 *   }
 * }
 * ```
 */

/**
 * FieldStatus variant type. Extensible via module augmentation of FieldStatusVariantMap.
 */

/**
 * A status message component for form fields.
 *
 * @example
 * ```
 * <FieldStatus
 *   type="error"
 *   message="This field is required"
 * />
 * <FieldStatus
 *   type="warning"
 *   message="This will be visible to others"
 *   variant="detached"
 * />
 * ```
 */
export function FieldStatus({
  ref,
  type,
  message,
  id,
  variant = 'attached',
  xstyle,
  className,
  style,
  ...rest
}) {
  const entryStyle = useEntryAnimation('slideDown');
  return /*#__PURE__*/_jsx("div", {
    ref: ref,
    id: id,
    role: type === 'error' ? 'alert' : 'status',
    "aria-live": type === 'error' ? 'assertive' : 'polite',
    ...rest,
    ...mergeProps(themeProps('field-status', {
      type,
      variant
    }), stylex.props(styles.base, entryStyle, variant === 'attached' ? styles.attached : styles.detached, colorStyles[type], xstyle), className, style),
    children: message
  });
}
FieldStatus.displayName = 'FieldStatus';